import { DailyActivity } from '../types';

const STORAGE_KEY = 'solo_leveling_statistics';

const getTodayDateString = (): string => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

export const statisticsService = {
  // Get all statistics
  getStatistics: (): DailyActivity[] => {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  },

  // Save statistics
  saveStatistics: (stats: DailyActivity[]): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  },

  // Load statistics (for Firestore sync)
  loadStatistics: (stats: DailyActivity[]): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  },

  // Get today's statistics
  getTodayStats: (): DailyActivity | null => {
    const stats = statisticsService.getStatistics();
    const today = getTodayDateString();
    return stats.find(s => s.date === today) || null;
  },

  // Update today's statistics
  updateTodayStats: (
    questsCompleted: number = 0,
    xpEarned: number = 0,
    focusMinutes: number = 0,
    currencyEarned: number = 0
  ): void => {
    const stats = statisticsService.getStatistics();
    const today = getTodayDateString();
    
    const todayIndex = stats.findIndex(s => s.date === today);
    
    if (todayIndex >= 0) {
      // Update existing entry
      stats[todayIndex].questsCompleted += questsCompleted;
      stats[todayIndex].xpEarned += xpEarned;
      stats[todayIndex].focusMinutes += focusMinutes;
      stats[todayIndex].currencyEarned += currencyEarned;
    } else {
      // Create new entry
      stats.push({
        date: today,
        questsCompleted,
        xpEarned,
        focusMinutes,
        currencyEarned,
      });
    }

    // Keep only last 90 days
    const sortedStats = stats.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 90);
    statisticsService.saveStatistics(sortedStats);
  },

  // Get statistics for last N days (fills missing days with 0s)
  getLastNDays: (days: number): DailyActivity[] => {
    const stats = statisticsService.getStatistics();
    const result: DailyActivity[] = [];
    
    // Generate last N days
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      // Find existing data or create empty entry
      const existingData = stats.find(s => s.date === dateString);
      result.push(existingData || {
        date: dateString,
        questsCompleted: 0,
        xpEarned: 0,
        focusMinutes: 0,
        currencyEarned: 0,
      });
    }
    
    return result;
  },

  // Get total statistics
  getTotals: (): {
    totalQuests: number;
    totalXP: number;
    totalFocusTime: number;
    totalCurrency: number;
  } => {
    const stats = statisticsService.getStatistics();
    return {
      totalQuests: stats.reduce((sum, s) => sum + s.questsCompleted, 0),
      totalXP: stats.reduce((sum, s) => sum + s.xpEarned, 0),
      totalFocusTime: stats.reduce((sum, s) => sum + s.focusMinutes, 0),
      totalCurrency: stats.reduce((sum, s) => sum + s.currencyEarned, 0),
    };
  },

  // Get weekly summary
  getWeeklySummary: (): DailyActivity[] => {
    return statisticsService.getLastNDays(7);
  },

  // Get monthly summary
  getMonthlySummary: (): DailyActivity[] => {
    return statisticsService.getLastNDays(30);
  },
};

